import React, { useEffect, useRef, useState } from 'react';
import { FiArrowUpRight, FiFileText, FiUpload } from 'react-icons/fi';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../../firebaseConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { safeExternalUrl } from '../opportunities/previewData';
import { regenerateScoutFromCv } from '../scoutApi';
import InlineCvPreview from './InlineCvPreview';

export default function ProfileCvCard({ user, profile, onProfileSaved }) {
  const { language } = useLanguage();
  const copy = (en, ar) => language === 'ar' ? ar : en;
  const input = useRef(null);
  const [savedCv, setSavedCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resolvedCv, setResolvedCv] = useState(null);
  const metadata = savedCv || profile?.cvMetadata;
  const storedUrl = safeExternalUrl(metadata?.downloadUrl || profile?.cv?.Link || profile?.cvUrl);
  const storagePath = metadata?.storagePath;
  const resolved = resolvedCv?.path === storagePath ? resolvedCv : null;
  const url = storedUrl || resolved?.url || '';
  useEffect(() => {
    if (storedUrl || !storagePath) return undefined;
    let cancelled = false;
    getDownloadURL(storageRef(storage, storagePath))
      .then((value) => { if (!cancelled) setResolvedCv({ path: storagePath, url: safeExternalUrl(value) }); })
      .catch(() => { if (!cancelled) setResolvedCv({ path: storagePath, url: '' }); });
    return () => { cancelled = true; };
  }, [storedUrl, storagePath]);
  const hasCv = Boolean(url || metadata?.storagePath || metadata?.name);
  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user?.uid || !onProfileSaved || busy) return;
    setError(''); setNotice('');
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError(copy('Choose a PDF file.', 'اختر ملفاً بصيغة PDF.')); return;
    }
    setBusy(true);
    try {
      // A failed profile save must not overwrite the user's previous CV file.
      const path = `Images/${user.uid}/cv-${Date.now()}.pdf`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, file, { contentType: 'application/pdf' });
      const downloadUrl = await getDownloadURL(fileRef);
      const next = { name: file.name, size: file.size, type: 'application/pdf', storagePath: path, downloadUrl, capturedAt: Date.now() };
      await onProfileSaved({ cv: { ...(profile?.cv || {}), Link: downloadUrl }, cvUrl: downloadUrl, cvMetadata: next });
      setSavedCv(next);
      setNotice(copy('CV saved.', 'تم حفظ السيرة الذاتية.'));
      if (profile?.primaryScoutId) {
        try {
          await regenerateScoutFromCv({ user, scoutId: profile.primaryScoutId });
          setNotice(copy('CV saved. Your job matches are being refreshed.', 'تم حفظ السيرة الذاتية. جارٍ تحديث الوظائف المطابقة لك.'));
        } catch {
          setNotice(copy('CV saved. Job matches could not be refreshed right now.', 'تم حفظ السيرة الذاتية، لكن تعذّر تحديث الوظائف المطابقة حالياً.'));
        }
      }
    } catch {
      setError(copy('Could not save your CV. Please try again.', 'تعذّر حفظ سيرتك الذاتية. حاول مجدداً.'));
    } finally { setBusy(false); }
  };
  return <section className="js-profile-v2__card js-profile-cv" aria-labelledby="profile-cv-title">
    <header className="js-profile-cv__header">
    <div className="js-profile-cv__document">
      <div><h2 id="profile-cv-title">{copy('CV', 'السيرة الذاتية')}</h2><p className={hasCv ? 'js-profile-cv__filename' : undefined} title={metadata?.name}><bdi>{metadata?.name || (hasCv ? copy('Uploaded CV', 'السيرة الذاتية المحمّلة') : copy('Add your CV to support your job search.', 'أضف سيرتك الذاتية لدعم بحثك عن وظيفة.'))}</bdi></p></div>
    </div>
    <div className="js-profile-cv__actions">
      {url && <a href={url} target="_blank" rel="noopener noreferrer">{copy('View CV', 'عرض السيرة الذاتية')}<FiArrowUpRight aria-hidden="true" /></a>}
      <button type="button" disabled={busy || !user?.uid || !onProfileSaved} onClick={() => input.current?.click()}><FiUpload aria-hidden="true" />{busy ? copy('Saving…', 'جارٍ الحفظ…') : hasCv ? copy('Replace CV', 'استبدال السيرة الذاتية') : copy('Upload CV', 'تحميل السيرة الذاتية')}</button>
    </div>
    </header>
    <input ref={input} type="file" hidden accept=".pdf,application/pdf" aria-label={copy('Upload a PDF CV', 'تحميل سيرة ذاتية بصيغة PDF')} onChange={upload} />
    {notice && <p className="js-profile-cv__feedback" role="status">{notice}</p>}
    {error && <p className="js-profile-cv__feedback is-error" role="alert">{error}</p>}
    {url ? <InlineCvPreview key={url} url={url} /> : storagePath && !resolved ? <div className="js-profile-cv__empty" role="status">{copy('Loading your CV…', 'جارٍ تحميل سيرتك الذاتية…')}</div> : <div className="js-profile-cv__empty"><FiFileText aria-hidden="true" /><p>{hasCv ? copy('The preview is unavailable for this file. Upload a PDF to display your CV here.', 'معاينة هذا الملف غير متاحة. حمّل ملف PDF لعرض سيرتك الذاتية هنا.') : copy('Your CV will appear here after you upload it.', 'ستظهر سيرتك الذاتية هنا بعد تحميلها.')}</p></div>}
  </section>;
}
